import axios, { Axios } from "axios";
import { phone } from "phone";

import {
  DecideFormInput,
  DecideServiceInterface,
  IConfirmSignup,
  RegisterResponseInterface,
} from "../types/decide";
import abbreviations from "../utils/abbreviations";

class Decide implements DecideServiceInterface {
  request: Axios;

  constructor(baseURL: string) {
    this.request = axios.create({
      baseURL,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }
  private formatPhoneNumber(str: string): string {
    const result = phone(str);
    return result.phoneNumber || "";
  }

  async getDomainSuggestion(companyName: string) {
    const abbreviateString = (businessName: string) => {
      // Remove all spaces from the string
      const str = businessName.replace(/\s/g, "");

      // Abbreviate the string to 12 characters
      if (str.length > 12) {
        return str.substring(0, 12);
      } else {
        return str;
      }
    };

    const words = companyName.toLowerCase().split(" ");

    let suggestion = (await abbreviateString(companyName)).toLowerCase();

    // First, we iterate through the words of the string.
    words.forEach((word, index, splittedWords) => {
      // If the word is an abbreviation, we set the suggestion to be the abbreviated string, and break out of the loop.
      if (abbreviations[word]) {
        suggestion = abbreviateString(
          splittedWords[0].substring(
            0,
            abbreviations[word].length === 4 ? 8 : 9
          ) + abbreviations[word]
        );

        return;
      }

      if (index === 0) {
        suggestion = word;
      }
    });

    return suggestion;
  }

  handleResponseError = (response: { success: any; message: any; errors: any[]; error: any; }) => {
    if (!response) {
      return 'There was an error';
    }
  
    if (!response.success && response.message) {
      return response.message;
    }
  
    if (response.errors || response.error) {
      let message = '';
      response.errors.filter((v: { message: string; }) => (message += '\n' + '\n' + v.message));
      return message;
    }
  };

  async register(payload: DecideFormInput): Promise<RegisterResponseInterface> {
    let phoneNumber = "";
    if (payload.phoneNumber && payload.countryCode) {
      phoneNumber = this.formatPhoneNumber(
        `${payload?.countryCode}-${payload?.phoneNumber}`
      );
    }

    delete payload.countryCode;
    try {
      const {
        data: { statusCode, data: response },
      } = await this.request.post(`/account/v2/signup`, {
        ...payload,
        phoneNumber,
      });
      if (statusCode !== 200) {
        return {
          status: "error",
          errorMessage:
            response?.message ||
            "There was a technical error. If error persist, contact support@indicina.co",
        };
      }

      return {
        status: "success",
        data: response,
      };
    } catch (err) {
      if (!axios.isAxiosError(err)) {
        return {
          status: "error",
          errorMessage:
            "There was a technical error. If error persist, contact support@indicina.co",
        };
      }

      const errorMessage = err.response?.data?.message;

      if (errorMessage) {
        return { status: "error", errorMessage };
      }

      let errors: string | undefined = "";
      if (err?.response?.data?.errors) {
        err?.response?.data?.errors?.forEach(
          (error: { field?: string; message?: string }) => {
            errors += `${error?.message} `;
          }
        );
      }

      return {
        status: "error",
        errorMessage:
          errors.trim() ||
          "There was a technical error. If error persist, contact support@indicina.co",
      };
    }
  }

  async slugValidator(slug: string): Promise<boolean> {
    return this.request
      .get(`/account/slug/${slug}/validate`)
      .then((response) => {
        return !(response.status !== 200 || !response.data?.data?.isValid);
      })
      .catch(() => {
        return false;
      });
  }



  handleApiErrors = (error: { message: string; response: { data: any; }; }) => {
    const message = error?.message ?? '';
  
    if (error.response) {
      if (error.response.data) {
        return error.response.data;
      }
    }
  
    return message;
  };

    async confirmSignup (payload: IConfirmSignup) {
    return this.request
      .post(`/account/confirmSignup`, {
        ...payload,
      })
      .catch((error) => {
        return this.handleApiErrors(error);
      });
  };
  
  async resendOtp (verificationId: string) {
    return this.request
      .post(`/account/resend-otp`, {
        verificationId,
      })
      .catch((error) => {
        return this.handleApiErrors(error);
      });
  };
}

export default Decide;
